import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, CheckCircle2, Sparkles } from "lucide-react";
import { SECTOR_DETAILS } from "@/data/sectorDetails";
import { SECTORS } from "@/data/content";
import { useSeo } from "@/hooks/useSeo";

export default function SectorDetail() {
  const { slug } = useParams();
  const detail = SECTOR_DETAILS[slug];

  // Always call hooks before any return to keep order consistent
  const canonical = `https://termal.datarapor.com/sektorler/${slug || ""}`;
  const sector = detail ? SECTORS.find((s) => s.id === detail.sectorId) : null;

  useSeo(
    detail
      ? {
          title: detail.title,
          description: detail.metaDescription,
          keywords: detail.keywords,
          canonical,
          image: sector?.image || "https://termal.datarapor.com/assets/zake-logo.png",
          jsonLd: {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Service",
                "@id": canonical + "#service",
                name: detail.h1,
                description: detail.metaDescription,
                provider: { "@id": "https://termal.datarapor.com/#organization" },
                areaServed: { "@type": "Country", name: "Türkiye" },
                serviceType: "Termal Görüntüleme ve Önleyici Güvenlik",
                url: canonical,
              },
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Anasayfa", item: "https://termal.datarapor.com/" },
                  { "@type": "ListItem", position: 2, name: "Sektörler", item: "https://termal.datarapor.com/sektorler" },
                  { "@type": "ListItem", position: 3, name: sector?.title || detail.h1, item: canonical },
                ],
              },
              {
                "@type": "FAQPage",
                mainEntity: detail.faq.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
            ],
          },
        }
      : {}
  );

  if (!detail) return <Navigate to="/sektorler" replace />;

  return (
    <div data-testid={`sector-detail-${slug}`} className="relative">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div
          className="absolute inset-0 opacity-30 bg-cover bg-center"
          style={{ backgroundImage: `url(${sector.image})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#06090F] via-[#06090F]/85 to-[#1a0b05]/80" />
        <div className="absolute inset-0 bg-grid opacity-30" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-14 md:pb-20">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-thermal-orange mb-4 flex items-center gap-3">
            <Link to="/sektorler" className="hover:text-thermal-yellow">
              / Sektörler
            </Link>
            <span className="text-slate-600">/</span>
            <span className="text-slate-400">{sector.category}</span>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-[2.25rem] sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05] max-w-4xl"
          >
            {detail.h1}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15 }}
            className="mt-6 max-w-3xl text-slate-300 text-base sm:text-lg leading-relaxed"
            dangerouslySetInnerHTML={{
              __html: detail.intro.replace(/\*\*(.+?)\*\*/g, '<strong class="text-thermal-orange font-semibold">$1</strong>'),
            }}
          />

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to="/risk-hesaplayici"
              className="inline-flex items-center justify-center gap-2 bg-thermal-orange hover:bg-thermal-red text-white px-6 py-3.5 font-mono text-xs uppercase tracking-[0.2em] transition-colors min-h-[48px]"
              data-testid="sector-cta-calc"
            >
              <Sparkles className="w-4 h-4" /> Risk Skorunu Hesapla
            </Link>
            <Link
              to="/iletisim"
              className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-thermal-orange hover:text-thermal-orange text-white px-6 py-3.5 font-mono text-xs uppercase tracking-[0.2em] transition-colors min-h-[48px]"
              data-testid="sector-cta-contact"
            >
              Demo Talep Et <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="mt-10 inline-flex items-center gap-4 border border-thermal-orange/40 px-5 py-3 bg-[#06090F]/60 backdrop-blur">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
              Anahtar Metrik
            </div>
            <div className="font-mono text-2xl text-thermal-yellow font-bold">
              {detail.metricBox.value}
            </div>
            <div className="text-sm text-slate-300">— {detail.metricBox.label}</div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-24 max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-thermal-orange mb-3">
          / Çözümün Bileşenleri
        </div>
        <h2 className="text-3xl md:text-5xl font-bold tracking-tight max-w-3xl">
          Sahada doğrulanmış kazanımlar
        </h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {detail.benefits.map((b, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="bg-[#06090F] p-6 md:p-7 hover:bg-[#0D131F] transition-colors group"
            >
              <div className="flex items-start justify-between mb-4">
                <CheckCircle2
                  className="w-5 h-5 text-thermal-orange shrink-0"
                  strokeWidth={1.8}
                />
                <span className="font-mono text-[10px] text-slate-500">
                  / 0{i + 1}
                </span>
              </div>
              <h3 className="text-lg font-semibold mb-2 group-hover:text-thermal-orange transition-colors">
                {b.title}
              </h3>
              <p className="text-slate-400 text-sm leading-relaxed">{b.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 border-y border-white/10 bg-[#0D131F]/30">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-thermal-orange mb-3">
            / Sıkça Sorulan Sorular — {sector?.title}
          </div>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-10">
            Sektör spesifik sorular
          </h2>
          <div className="space-y-px bg-white/5 border border-white/10">
            {detail.faq.map((item, i) => (
              <details
                key={i}
                data-testid={`sector-faq-${i}`}
                className="bg-[#06090F] group p-5 md:p-6 [&[open]]:bg-[#0D131F] transition-colors"
              >
                <summary className="cursor-pointer flex items-start justify-between gap-4 list-none">
                  <div className="flex items-start gap-4 flex-1 min-w-0">
                    <span className="font-mono text-thermal-orange text-xs mt-1 shrink-0">
                      Q{String(i + 1).padStart(2, "0")}
                    </span>
                    <h3 className="text-base md:text-lg font-semibold text-white leading-snug">
                      {item.q}
                    </h3>
                  </div>
                  <span className="font-mono text-thermal-orange text-xl group-open:rotate-45 transition-transform shrink-0 leading-none">
                    +
                  </span>
                </summary>
                <p className="mt-4 ml-0 md:ml-12 text-slate-300 text-sm md:text-base leading-relaxed">
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Related sectors */}
      <section className="py-16 max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-thermal-orange mb-3">
          / İlgili Sektörler
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          {detail.relatedSectors.map((relSlug) => {
            const rd = SECTOR_DETAILS[relSlug];
            const rs = SECTORS.find((s) => s.id === rd.sectorId);
            return (
              <Link
                key={relSlug}
                to={`/sektorler/${relSlug}`}
                className="terminal-border p-5 hover:border-thermal-orange/60 transition-colors flex items-start justify-between gap-3 group"
              >
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    / {rs?.category}
                  </div>
                  <div className="mt-2 text-lg font-semibold group-hover:text-thermal-orange transition-colors">
                    {rs?.title}
                  </div>
                  <div className="mt-1 text-sm text-slate-400 line-clamp-2">
                    {rs?.summary}
                  </div>
                </div>
                <ArrowUpRight className="w-5 h-5 text-thermal-orange shrink-0 mt-1" />
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}
